import clsx from 'clsx';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Button, Headline, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {FILTERS_GROUPS_IDS} from '@/types/models/creator-crm/filterId';
import searchCreatorsGetters from '@/views/creators/creator-crm/utils/searchCreatorsGetters';
import SideDrawer from '@/components/side-drawer';
import Skeleton from '@/components/skeleton';
import useCreatorCrmStore from '@/stores/creator-crm/creatorCrmStore';
import styles from './ActionsDrawer.module.scss';
import Actions from './actions';

type FiltersGroupsMap = Record<string, {localeLabelKey: string}>;

function ActionsDrawerHeader({onClose}: {onClose: (label: string) => void}) {
  return (
    <div className={styles.header}>
      <Headline size="md">{translate('Filters')}</Headline>
      <Button
        className={styles.closeButton}
        size="small"
        appearance="neutral"
        mode="plain"
        onClick={() => onClose('Close')}
      >
        <Icon size="small" appearance="neutral" name="Actions-Close-Normal" />
      </Button>
    </div>
  );
}

function ActionsDrawerContent({
  filtersGroups,
  isSearchMode,
}: {
  filtersGroups: FiltersGroupsMap;
  isSearchMode?: boolean;
}) {
  return (
    <div className={styles.content}>
      {FILTERS_GROUPS_IDS.map((groupId) => (
        <div key={groupId} className={styles.filterSection}>
          <Headline size="sm" className={styles.sectionHeader}>
            {translate(filtersGroups[groupId].localeLabelKey)}
          </Headline>
          <Actions filtersGroupId={groupId} isSearchMode={isSearchMode} />
        </div>
      ))}
    </div>
  );
}

function ActionsDrawerFooter({
  clearAllDisabled,
  saveAsSegmentDisabled,
  onClearAll,
  onSaveFilters,
}: {
  clearAllDisabled: boolean;
  saveAsSegmentDisabled: boolean;
  onClearAll: (label: string) => void;
  onSaveFilters?: () => void;
}) {
  return (
    <div className={styles.footer}>
      <Button
        size="medium"
        appearance="neutral"
        mode="filled"
        disabled={saveAsSegmentDisabled}
        onClick={onSaveFilters}
        className={styles.saveFiltersButton}
      >
        {translate('Save Filters')}
      </Button>
      <Button
        size="small"
        appearance="danger"
        mode="plain"
        disabled={clearAllDisabled}
        onClick={() => onClearAll(translate('Clear all'))}
      >
        {translate('Clear All')}
      </Button>
    </div>
  );
}

function ActionsDrawerFiltersSkeleton() {
  return (
    <>
      <div className={styles.header}>
        <Skeleton variant="text" width={72} height={24} />
        <Skeleton variant="circular" width={36} height={36} />
      </div>
      <div className={styles.content}>
        {/* Creator Details group: section heading + 4 filter rows */}
        <div className={styles.filterSection}>
          <div className={styles.skeletonSectionHeader}>
            <Skeleton variant="text" width={140} height={20} />
          </div>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.skeletonFilterRow}>
              <Skeleton variant="rounded" width="100%" height={40} />
            </div>
          ))}
        </div>
        {/* Platforms group: section heading + 2 filter rows */}
        <div className={styles.filterSection}>
          <div className={styles.skeletonSectionHeader}>
            <Skeleton variant="text" width={100} height={20} />
          </div>
          {[1, 2].map((i) => (
            <div key={i} className={styles.skeletonFilterRow}>
              <Skeleton variant="rounded" width="100%" height={40} />
            </div>
          ))}
        </div>
      </div>
      <div className={styles.footer}>
        <Skeleton
          variant="rounded"
          width="100%"
          height={40}
          sx={{borderRadius: '999px'}}
        />
        <Skeleton
          variant="text"
          width={80}
          height={24}
          sx={{alignSelf: 'center'}}
        />
      </div>
    </>
  );
}

function useDeferredDrawerContent(isOpen: boolean) {
  const [contentReady, setContentReady] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true;
      requestAnimationFrame(() => {
        setContentReady(true);
        requestAnimationFrame(() => setContentVisible(true));
      });
    }
    if (!isOpen) {
      wasOpenRef.current = false;
      setContentReady(false);
      setContentVisible(false);
    }
  }, [isOpen]);
  return {contentReady, contentVisible};
}

export type ActionsDrawerProps = {
  testID?: string;
  canSaveSegment: boolean;
  onClose: (label: string) => void;
  onClearAll: (label: string) => void;
  onSaveAsSegment?: () => void;
  onPresentedOrDismissed?: (
    presentedOrDismissed: 'presented' | 'dismissed',
    name: string
  ) => void;
  isSearchMode?: boolean;
};

function ActionsDrawer(props: ActionsDrawerProps) {
  const {
    testID = 'actions-drawer',
    canSaveSegment,
    onClose,
    onClearAll,
    onSaveAsSegment,
    onPresentedOrDismissed,
    isSearchMode,
  } = props;
  const [isFiltersDrawerOpen, filtersGroups, searchChipsLength] =
    useCreatorCrmStore(
      useShallow((state) => [
        state.content.filtersState.isFiltersDrawerOpen,
        state.content.filtersState.filtersGroups,
        state.content.filtersState.searchState?.chips?.length ?? 0,
      ])
    );
  const {contentReady, contentVisible} =
    useDeferredDrawerContent(isFiltersDrawerOpen);

  const totalActiveFilters = useMemo(
    () => searchCreatorsGetters.getTotalActiveFiltersCount(filtersGroups),
    [filtersGroups]
  );
  const hasSearchChips = searchChipsLength > 0;
  const clearAllDisabled = totalActiveFilters === 0 && !hasSearchChips;

  useEffect(() => {
    if (isFiltersDrawerOpen) {
      onPresentedOrDismissed?.('presented', 'Filters');
    }
    return () => {
      if (isFiltersDrawerOpen) {
        onPresentedOrDismissed?.('dismissed', 'Filters');
      }
    };
  }, [isFiltersDrawerOpen, onPresentedOrDismissed]);

  const showSkeleton = isFiltersDrawerOpen && !contentReady;
  const showContent = isFiltersDrawerOpen && contentReady;

  return (
    <SideDrawer
      testID={testID}
      isOpen={isFiltersDrawerOpen}
      onClose={() => onClose('')}
      drawerClassName={styles.actionsDrawer}
    >
      {showSkeleton ? (
        <div
          className={clsx(
            styles.actionsDrawerContainer,
            styles.skeletonVisible
          )}
        >
          <ActionsDrawerFiltersSkeleton />
        </div>
      ) : null}
      {showContent ? (
        <div
          className={clsx(
            styles.actionsDrawerContainer,
            contentVisible && styles.contentVisible
          )}
        >
          <ActionsDrawerHeader onClose={onClose} />
          <ActionsDrawerContent
            filtersGroups={filtersGroups}
            isSearchMode={isSearchMode}
          />
          <ActionsDrawerFooter
            clearAllDisabled={clearAllDisabled}
            saveAsSegmentDisabled={!canSaveSegment}
            onClearAll={onClearAll}
            onSaveFilters={onSaveAsSegment}
          />
        </div>
      ) : null}
    </SideDrawer>
  );
}

export default ActionsDrawer;
